import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { cityService } from '../services/api';
import { EditCityModal, AddRouteModal, PipelineConfirmModal } from '../components/CityModals';
import HexMap from '../components/HexMap';
import CityGoLiveStepper from '../components/CityGoLiveStepper';
import {
  ArrowLeft, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Activity, Cloud, Wind, Car, MapPin, Tag,
  AlertTriangle, XCircle, RefreshCw, Trash2, Settings,
  Play, Gauge, HardDrive,
  Layers, Eye, X
} from 'lucide-react';
import './CityDashboard.css';

// ════════════════════════════════════════════════════════════════
// CityDetail — Command Center Layout
// ════════════════════════════════════════════════════════════════
const CityDetail = () => {
  const { cityId } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Layout
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [bottomTab, setBottomTab] = useState(null);
  const [bottomExpanded, setBottomExpanded] = useState(false);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddRouteModal, setShowAddRouteModal] = useState(false);
  const [pipelineConfirm, setPipelineConfirm] = useState(null);

  // Monitoring data (loaded automatically)
  const [signalFreshness, setSignalFreshness] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [redisStats, setRedisStats] = useState(null);

  // Tab data (lazy-loaded)
  const [readings, setReadings] = useState(null);
  const [readingsPagination, setReadingsPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 0 });
  const [trafficDist, setTrafficDist] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);

  // ── Fetch Functions ──────────────────────────────────
  const fetchCity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cityService.getById(cityId, { include: 'routes,hexstats,calibrations' });
      setCity(response.data);
    } catch (err) {
      console.error('Error fetching city:', err);
      setError('Failed to load city details');
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  const fetchSignalFreshness = useCallback(async () => {
    try { const response = await cityService.getSignalFreshness(cityId); setSignalFreshness(response.data); }
    catch (err) { console.error('Error fetching signal freshness:', err); }
  }, [cityId]);

  const fetchTrafficStats = useCallback(async () => {
    try {
      const response = await cityService.getById(cityId, { include: 'traffic' });
      if (response.data?.trafficStats) {
        setCity(prev => prev ? { ...prev, trafficStats: response.data.trafficStats } : prev);
      }
    } catch (err) { console.error('Error fetching traffic stats:', err); }
  }, [cityId]);

  const fetchRedisStats = useCallback(async () => {
    try { const response = await cityService.getRedisStats(); setRedisStats(response.data); }
    catch (err) { console.error('Error fetching Redis stats:', err); }
  }, []);

  const fetchDataQuality = useCallback(async () => {
    try { const response = await cityService.getDataQuality(cityId); setDataQuality(response.data); }
    catch (err) { console.error('Error fetching data quality:', err); }
  }, [cityId]);

  const fetchReadings = useCallback(async () => {
    try {
      const response = await cityService.getSentinelReadings(cityId, { page: readingsPagination.page, pageSize: readingsPagination.pageSize });
      setReadings(response.data || []);
      if (response.pagination) setReadingsPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (err) { console.error('Error fetching readings:', err); }
  }, [cityId, readingsPagination.page, readingsPagination.pageSize]);

  const fetchTrafficDist = useCallback(async () => {
    try {
      const response = await cityService.getTrafficDistribution(cityId, { hours: 24 });
      setTrafficDist(response.data);
    } catch (err) { console.error('Error fetching traffic distribution:', err); }
  }, [cityId]);

  const fetchCost = useCallback(async () => {
    try { const response = await cityService.getCostEstimate(cityId); setCostEstimate(response.data); }
    catch (err) { console.error('Error fetching cost:', err); }
  }, [cityId]);

  // ── Effects ──────────────────────────────────────────
  useEffect(() => { fetchCity(); }, [fetchCity]);

  useEffect(() => {
    if (!city) return;
    fetchSignalFreshness();
    fetchRedisStats();
    fetchTrafficStats();
  }, [city, fetchSignalFreshness, fetchRedisStats, fetchTrafficStats]);

  useEffect(() => {
    if (bottomTab === 'readings' && !readings) fetchReadings();
    if (bottomTab === 'traffic' && !trafficDist) fetchTrafficDist();
    if (bottomTab === 'cost' && !costEstimate) fetchCost();
    if (bottomTab === 'quality' && !dataQuality) fetchDataQuality();
  }, [bottomTab, readings, trafficDist, costEstimate, dataQuality, fetchReadings, fetchTrafficDist, fetchCost, fetchDataQuality]);

  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => { setMessage(null); setError(null); }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // ── Handlers ─────────────────────────────────────────
  const handlePipeline = async (action) => {
    setActionLoading(action);
    try {
      let result;
      switch (action) {
        case 'seed': result = await cityService.triggerSeed(cityId); break;
        case 'enrich': result = await cityService.triggerEnrich(cityId); break;
        case 'calibrate': result = await cityService.triggerCalibrate(cityId); break;
        case 'poll': result = await cityService.triggerPoll(cityId); break;
        case 'weather': result = await cityService.triggerWeather(cityId); break;
        case 'aqi': result = await cityService.triggerAqi(cityId); break;
        case 'scene': result = await cityService.triggerScene(cityId); break;
        case 'scene-res10': result = await cityService.triggerSceneRes10(cityId); break;
        case 'derive-labels': result = await cityService.triggerDeriveLabels(cityId); break;
        case 'manage-partitions': result = await cityService.triggerManagePartitions(cityId); break;
        default: return;
      }
      setMessage(result.message || `Pipeline "${action}" triggered`);
      setPipelineConfirm(null);
      setTimeout(() => fetchCity(), 3000);
    } catch (err) {
      setError(err.response?.data?.error || `Failed to trigger ${action}`);
      setPipelineConfirm(null);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRoute = async (routeId, routeName) => {
    if (!window.confirm(`Remove sentinel route "${routeName}"?`)) return;
    try {
      await cityService.deleteRoute(cityId, routeId);
      setMessage(`Route "${routeName}" removed`);
      fetchCity();
    } catch (err) { setError(err.response?.data?.error || 'Failed to remove route'); }
  };

  const handleStatusChange = async (newStatus) => {
    setActionLoading('status');
    try {
      await cityService.update(cityId, { status: newStatus });
      setMessage(`City status changed to ${newStatus}`);
      fetchCity();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update status'); }
    finally { setActionLoading(null); }
  };

  const handleFlushRedis = async () => {
    if (!window.confirm('Purge ALL Redis cache keys for this city? This forces fresh data on next poll.')) return;
    setActionLoading('flush-redis');
    try {
      const result = await cityService.flushCityRedis(cityId);
      setMessage(result.message);
      setRedisStats(null);
      fetchRedisStats();
    } catch (err) { setError('Failed to flush Redis'); }
    finally { setActionLoading(null); }
  };

  // ── Computed ─────────────────────────────────────────
  const healthScore = useMemo(() => {
    if (!signalFreshness || !city) return null;
    const { signals } = signalFreshness;
    if (!signals || signals.length === 0) return null;
    let score = 100;
    for (const s of signals) {
      if (s.coveragePct < 50) score -= 15;
      else if (s.coveragePct < 80) score -= 5;
      if (s.missedRuns > 5) score -= 20;
      else if (s.missedRuns > 0) score -= 5;
    }
    return Math.max(0, Math.min(100, score));
  }, [signalFreshness, city]);

  const alerts = useMemo(() => {
    const a = [];
    if (signalFreshness?.signals) {
      for (const s of signalFreshness.signals) {
        if (s.missedRuns > 5) a.push({ level: 'critical', msg: `${s.signalType}: ${s.missedRuns} missed poll runs` });
        else if (s.missedRuns > 0) a.push({ level: 'warning', msg: `${s.signalType}: ${s.missedRuns} missed runs` });
        if (s.coveragePct < 50) a.push({ level: 'critical', msg: `${s.signalType}: only ${s.coveragePct}% coverage` });
      }
    }
    if (dataQuality?.enrichment) {
      const unenriched = dataQuality.enrichment.reduce((acc, r) => acc + r.neverEnriched, 0);
      if (unenriched > 0) a.push({ level: 'warning', msg: `${unenriched.toLocaleString()} unenriched hexes` });
    }
    return a;
  }, [signalFreshness, dataQuality]);

  // ── Helpers ──────────────────────────────────────────
  const fmtNum = (n) => (n == null ? '0' : n.toLocaleString());
  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : 'N/A');
  const fmtRel = (d) => {
    if (!d) return 'Never';
    const ms = Date.now() - new Date(d).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  // ── Loading/Error ────────────────────────────────────
  if (loading) return (
    <div className="city-dashboard">
      <div className="cd-loading"><div className="cd-spinner" /></div>
    </div>
  );

  if (!city) return (
    <div className="city-dashboard" style={{ padding: 40 }}>
      <div style={{ padding: 16, borderRadius: 8, background: 'rgba(239,68,68,0.15)', color: '#ef4444', marginBottom: 16 }}>City not found</div>
      <Link to="/cities" style={{ color: '#3b82f6' }}>← Back to Cities</Link>
    </div>
  );

  const statusClass = `cd-status-${city.status}`;
  const BOTTOM_TABS = [
    { key: 'hex', label: 'Hex Grid' },
    { key: 'routes', label: `Routes (${city.routes?.length || 0})` },
    { key: 'calibrations', label: 'Calibrations' },
    { key: 'readings', label: 'Readings' },
    { key: 'traffic', label: 'Traffic' },
    { key: 'signals', label: 'Signal Health' },
    { key: 'quality', label: 'Data Quality' },
    { key: 'cost', label: 'Cost' },
  ];

  return (
    <div className="city-dashboard">
      {message && <div className="cd-toast cd-toast-success">{message}</div>}
      {error && <div className="cd-toast cd-toast-error">{error}</div>}

      {/* Header */}
      <div className="cd-header">
        <div className="cd-header-left">
          <Link to="/cities" className="cd-back-link"><ArrowLeft size={14} /> Cities</Link>
          <div>
            <h1 className="cd-city-name">
              {city.name}
              <span className={`cd-status ${statusClass}`} style={{ marginLeft: 12 }}>{city.status}</span>
            </h1>
            <div className="cd-city-meta">
              {city.countryCode} · {city.timezone} · UTC{parseFloat(city.utcOffset) >= 0 ? '+' : ''}{city.utcOffset}
              {city.hexCountTotal > 0 && <> · {fmtNum(city.hexCountTotal)} hexes</>}
            </div>
          </div>
          {healthScore !== null && (
            <div className={`cd-health-badge ${healthScore >= 80 ? 'cd-health-good' : healthScore >= 50 ? 'cd-health-warn' : 'cd-health-bad'}`}>
              <Activity size={14} />
              {healthScore}%
            </div>
          )}
        </div>
        <div className="cd-header-actions">
          {alerts.length > 0 && (
            <span style={{ fontSize: '0.75rem', color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertTriangle size={14} /> {alerts.length}
            </span>
          )}
          <button className="cd-btn" onClick={() => setShowEditModal(true)}><Settings size={14} /> Edit</button>
          {city.status === 'active' && (
            <button className="cd-btn cd-btn-danger" onClick={() => handleStatusChange('paused')} disabled={actionLoading === 'status'}>Pause</button>
          )}
          {city.status === 'paused' && (
            <button className="cd-btn cd-btn-primary" onClick={() => handleStatusChange('active')} disabled={actionLoading === 'status'}>Resume</button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="cd-body">
        {/* Sidebar */}
        <div className={`cd-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="cd-sidebar-section">
            <div className="cd-sidebar-section-title">Overview</div>
            <div className="cd-kpi-grid">
              <div className="cd-kpi-card accent">
                <div className="cd-kpi-label">Hexes</div>
                <div className="cd-kpi-value">{fmtNum(city.hexCountTotal)}</div>
                <div className="cd-kpi-sub">R8:{fmtNum(city.hexCountRes8)} R9:{fmtNum(city.hexCountRes9)} R10:{fmtNum(city.hexCountRes10)}</div>
              </div>
              <div className="cd-kpi-card success">
                <div className="cd-kpi-label">Road</div>
                <div className="cd-kpi-value">{fmtNum(city.roadHexCount)}</div>
                <div className="cd-kpi-sub">{city.hexCountTotal > 0 ? `${((city.roadHexCount / city.hexCountTotal) * 100).toFixed(1)}%` : '—'}</div>
              </div>
              <div className="cd-kpi-card">
                <div className="cd-kpi-label">Sentinels</div>
                <div className="cd-kpi-value">{city.sentinelCount}</div>
                <div className="cd-kpi-sub">{city.routes?.length || 0} routes</div>
              </div>
              <div className="cd-kpi-card">
                <div className="cd-kpi-label">Poll Rate</div>
                <div className="cd-kpi-value">{city.pollIntervalMinutes}m</div>
                <div className="cd-kpi-sub">{Math.round(60 / city.pollIntervalMinutes)}/hr</div>
              </div>
              {city.trafficStats && (<>
                <div className="cd-kpi-card warning">
                  <div className="cd-kpi-label">Snapshots</div>
                  <div className="cd-kpi-value">{fmtNum(city.trafficStats.totalSnapshots)}</div>
                  <div className="cd-kpi-sub">{fmtNum(city.trafficStats.hexesWithTraffic)} hexes</div>
                </div>
                <div className="cd-kpi-card">
                  <div className="cd-kpi-label">Active</div>
                  <div className="cd-kpi-value">{fmtNum(city.trafficStats.active)}</div>
                  <div className="cd-kpi-sub">{fmtNum(city.trafficStats.expired)} expired</div>
                </div>
              </>)}
            </div>
          </div>

          {/* Signal Health */}
          <div className="cd-sidebar-section">
            <div className="cd-sidebar-section-title">
              <span>Signal Health</span>
              <button className="cd-btn-icon" onClick={() => { setSignalFreshness(null); fetchSignalFreshness(); }} style={{ marginLeft: 'auto' }}>
                <RefreshCw size={11} />
              </button>
            </div>
            {!signalFreshness ? (
              <div style={{ color: '#64748b', fontSize: '0.75rem', textAlign: 'center', padding: 8 }}>Loading...</div>
            ) : (
              <div className="cd-signal-list">
                {signalFreshness.signals.map(s => {
                  const indicator = s.missedRuns > 5 ? 'dead' : s.missedRuns > 0 ? 'stale' : s.active > 0 ? 'live' : 'none';
                  const icons = { weather: <Cloud size={13} />, traffic_density: <Car size={13} />, aqi: <Wind size={13} />, scene_context: <MapPin size={13} /> };
                  return (
                    <div key={s.signalType} className="cd-signal-card">
                      <div className={`cd-signal-indicator ${indicator}`} />
                      <div className="cd-signal-info">
                        <div className="cd-signal-name">{icons[s.signalType]} {s.signalType.replace(/_/g, ' ')}</div>
                        <div className="cd-signal-meta">
                          {fmtRel(s.lastSnapshot)} · {s.cadence}
                          {s.missedRuns > 0 && <span style={{ color: '#ef4444' }}> · {s.missedRuns} missed</span>}
                        </div>
                      </div>
                      <div className="cd-signal-coverage" style={{ color: s.coveragePct >= 80 ? '#22c55e' : s.coveragePct >= 50 ? '#f59e0b' : '#ef4444' }}>
                        {s.coveragePct}%
                      </div>
                    </div>
                  );
                })}
                {/* Semantic-label sources (Ola, NRSC LULC, etc.) */}
                {signalFreshness.semantic?.map(s => {
                  const ageMs = s.lastVerified ? (Date.now() - new Date(s.lastVerified).getTime()) : null;
                  const stale =
                    s.cadence === 'Weekly'    && ageMs > 14 * 86400000 ? 'stale' :
                    s.cadence === 'Quarterly' && ageMs > 120 * 86400000 ? 'stale' :
                    s.cadence === 'Weekly'    && ageMs > 8  * 86400000 ? 'warn' :
                    s.cadence === 'Quarterly' && ageMs > 95 * 86400000 ? 'warn' :
                    s.lastVerified ? 'live' : 'none';
                  const indicator = stale === 'stale' ? 'dead' : stale === 'warn' ? 'stale' : 'live';
                  return (
                    <div key={`sem-${s.source}`} className="cd-signal-card">
                      <div className={`cd-signal-indicator ${indicator}`} />
                      <div className="cd-signal-info">
                        <div className="cd-signal-name"><MapPin size={13} /> {s.source.replace(/_/g, ' ')} labels</div>
                        <div className="cd-signal-meta">{fmtRel(s.lastVerified)} · {s.cadence} · {fmtNum(s.labelCount)} labels</div>
                      </div>
                      <div className="cd-signal-coverage" style={{ color: s.coveragePct >= 80 ? '#22c55e' : s.coveragePct >= 50 ? '#f59e0b' : '#ef4444' }}>
                        {s.coveragePct}%
                      </div>
                    </div>
                  );
                })}
                {/* Transit stops (OSM + GTFS) */}
                {signalFreshness.transit?.map(t => (
                  <div key={`trn-${t.source}`} className="cd-signal-card">
                    <div className={`cd-signal-indicator ${t.stopCount > 0 ? 'live' : 'none'}`} />
                    <div className="cd-signal-info">
                      <div className="cd-signal-name"><MapPin size={13} /> transit ({t.source})</div>
                      <div className="cd-signal-meta">{fmtRel(t.lastIngested)} · {t.cadence} · {fmtNum(t.stopCount)} stops</div>
                    </div>
                    <div className="cd-signal-coverage" style={{ color: t.coveragePct >= 50 ? '#22c55e' : t.coveragePct >= 20 ? '#f59e0b' : '#ef4444' }}>
                      {t.coveragePct}%
                    </div>
                  </div>
                ))}
                {/* Land/ocean classification */}
                {signalFreshness.land && (
                  <div className="cd-signal-card">
                    <div className={`cd-signal-indicator ${signalFreshness.land.classifiedPct >= 95 ? 'live' : 'stale'}`} />
                    <div className="cd-signal-info">
                      <div className="cd-signal-name"><MapPin size={13} /> land/ocean</div>
                      <div className="cd-signal-meta">
                        {fmtNum(signalFreshness.land.land)} land · {fmtNum(signalFreshness.land.ocean)} ocean · {fmtNum(signalFreshness.land.unknown)} unknown
                      </div>
                    </div>
                    <div className="cd-signal-coverage" style={{ color: signalFreshness.land.classifiedPct >= 95 ? '#22c55e' : '#f59e0b' }}>
                      {signalFreshness.land.classifiedPct}%
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Phase 1.A + 1.E SLO Status (read-only window) */}
          <div className="cd-sidebar-section">
            <div className="cd-sidebar-section-title">
              <span>SLO Status</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#64748b' }}>Phase 1.A · 1.E</span>
            </div>
            {!signalFreshness?.slos ? (
              <div style={{ color: '#64748b', fontSize: '0.75rem', textAlign: 'center', padding: 8 }}>Loading...</div>
            ) : (() => {
              const slos = signalFreshness.slos;
              const sloColor = (s) => s === 'green' ? '#22c55e' : s === 'amber' ? '#f59e0b' : s === 'red' ? '#ef4444' : '#64748b';
              return (<>
                <div style={{ padding: '4px 0 8px 0', fontSize: '0.7rem', color: '#94a3b8' }}>
                  <strong style={{ color: sloColor(slos.phase_1a.overall_status) }}>1.A freshness ({slos.phase_1a.window_days}d window):</strong>{' '}
                  {slos.phase_1a.green_count}/{slos.phase_1a.total_targets} green
                  {slos.phase_1a.amber_count > 0 && <span style={{ color: '#f59e0b' }}> · {slos.phase_1a.amber_count} amber</span>}
                  {slos.phase_1a.red_count > 0 && <span style={{ color: '#ef4444' }}> · {slos.phase_1a.red_count} red</span>}
                </div>
                <div className="cd-signal-list">
                  {slos.phase_1a.targets.map((t) => (
                    <div key={`slo-${t.name}`} className="cd-signal-card">
                      <div className="cd-signal-indicator" style={{ background: sloColor(t.status) }} />
                      <div className="cd-signal-info">
                        <div className="cd-signal-name">{t.name.replace(/_/g, ' ')}</div>
                        <div className="cd-signal-meta">target ≥{t.target_pct}% · {t.window} · {t.cadence}</div>
                      </div>
                      <div className="cd-signal-coverage" style={{ color: sloColor(t.status) }}>
                        {t.current_pct == null ? '—' : `${t.current_pct}%`}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '12px 0 4px 0', fontSize: '0.7rem', color: '#94a3b8' }}>
                  <strong style={{ color: sloColor(slos.phase_1e.overall_status) }}>1.E read-path p95 ({slos.phase_1e.window_days}d window):</strong>{' '}
                  pending load test
                </div>
                <div className="cd-signal-list">
                  {slos.phase_1e.targets.map((r) => (
                    <div key={`slo-1e-${r.endpoint}`} className="cd-signal-card">
                      <div className="cd-signal-indicator" style={{ background: sloColor(r.status === 'green' ? 'green' : 'pending') }} />
                      <div className="cd-signal-info">
                        <div className="cd-signal-name">{r.name}</div>
                        <div className="cd-signal-meta">budget ≤{r.p95_budget_ms}ms p95</div>
                      </div>
                      <div className="cd-signal-coverage" style={{ color: r.current_p95_ms == null ? '#64748b' : sloColor(r.status) }}>
                        {r.current_p95_ms == null ? '—' : `${r.current_p95_ms}ms`}
                      </div>
                    </div>
                  ))}
                </div>
              </>);
            })()}
          </div>

          {/* Redis Cache */}
          <div className="cd-sidebar-section">
            <div className="cd-sidebar-section-title">
              <span>Redis Cache</span>
              <button className="cd-btn-icon" onClick={() => { setRedisStats(null); fetchRedisStats(); }} style={{ marginLeft: 'auto' }}>
                <RefreshCw size={11} />
              </button>
            </div>
            {!redisStats ? (
              <div style={{ color: '#64748b', fontSize: '0.75rem', textAlign: 'center', padding: 8 }}>Loading...</div>
            ) : (<>
              <div className="cd-redis-stats">
                <div className="cd-redis-stat">
                  <div className="cd-redis-stat-label">Memory</div>
                  <div className="cd-redis-stat-value">{redisStats.memory.usedMemory}</div>
                </div>
                <div className="cd-redis-stat">
                  <div className="cd-redis-stat-label">Hex Keys</div>
                  <div className="cd-redis-stat-value">{fmtNum(redisStats.keys.totalHexKeys)}</div>
                </div>
                <div className="cd-redis-stat">
                  <div className="cd-redis-stat-label">Hit Rate</div>
                  <div className="cd-redis-stat-value" style={{ color: '#22c55e' }}>{redisStats.stats.hitRate || 'N/A'}</div>
                </div>
                <div className="cd-redis-stat">
                  <div className="cd-redis-stat-label">Evictions</div>
                  <div className="cd-redis-stat-value">{fmtNum(parseInt(redisStats.stats.evictedKeys || 0))}</div>
                </div>
              </div>
              <button className="cd-btn cd-btn-danger cd-btn-sm" style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}
                onClick={handleFlushRedis} disabled={actionLoading === 'flush-redis'}>
                <Trash2 size={11} /> {actionLoading === 'flush-redis' ? 'Flushing...' : 'Flush City Cache'}
              </button>
            </>)}
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="cd-sidebar-section">
              <div className="cd-sidebar-section-title">
                <AlertTriangle size={11} /> Alerts ({alerts.length})
              </div>
              {alerts.map((a, i) => (
                <div key={i} className={`cd-alert-item ${a.level}`}>
                  {a.level === 'critical'
                    ? <XCircle size={13} style={{ flexShrink: 0, color: '#ef4444', marginTop: 1 }} />
                    : <AlertTriangle size={13} style={{ flexShrink: 0, color: '#f59e0b', marginTop: 1 }} />}
                  <span>{a.msg}</span>
                </div>
              ))}
            </div>
          )}

          {/* Pipeline Actions */}
          <div className="cd-sidebar-section" style={{ borderBottom: 'none', flex: 1 }}>
            <div className="cd-sidebar-section-title">Pipeline Actions</div>
            <div className="cd-pipeline-group">
              <div className="cd-pipeline-group-label">Go-Live (FAANG state machine)</div>
              <CityGoLiveStepper cityId={cityId} />
            </div>
            <div className="cd-pipeline-group">
              <div className="cd-pipeline-group-label">City-Scoped (manual)</div>
              <div className="cd-pipeline-actions">
                <PipelineBtn action="seed" label="Seed" icon={<Layers size={11} />} desc="Generate H3 hex grid for res 8/9/10." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="enrich" label="Enrich" icon={<Tag size={11} />} desc="Classify road types via Google Nearest Roads API." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="calibrate" label="Calibrate" icon={<Gauge size={11} />} desc="Test all candidate routes and select best sentinels." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="poll" label="Poll Now" icon={<Play size={11} />} desc="Query sentinel routes for current traffic." variant="primary" actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
              </div>
            </div>
            <div className="cd-pipeline-group">
              <div className="cd-pipeline-group-label">Global (All Cities)</div>
              <div className="cd-pipeline-actions">
                <PipelineBtn action="weather" label="Weather" icon={<Cloud size={11} />} desc="Poll Google Weather API for all res-8 cells." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="aqi" label="AQI" icon={<Wind size={11} />} desc="Poll Google Air Quality API for all res-8 cells." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="scene" label="Scene" icon={<MapPin size={11} />} desc="Rich 3-API enrichment for all res-9 cells." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="scene-res10" label="Scene R10" icon={<Eye size={11} />} desc="Expand worthy res-9 parents to res-10 children." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="derive-labels" label="Labels" icon={<Tag size={11} />} desc="Derive ~20 semantic label types from scene context." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
                <PipelineBtn action="manage-partitions" label="Partitions" icon={<HardDrive size={11} />} desc="Create future partitions, detach old ones." actionLoading={actionLoading} onConfirm={setPipelineConfirm} />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Toggle */}
        <button className={`cd-sidebar-toggle ${sidebarCollapsed ? 'collapsed' : ''}`}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
          {sidebarCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>

        {/* Map Area */}
        <div className="cd-map-area">
          <HexMap cityId={cityId} city={city} />

          {/* Bottom Panel */}
          {bottomTab !== null && (
            <div className={`cd-bottom-panel ${!bottomExpanded ? 'collapsed' : ''}`}>
              <div className="cd-bottom-tab-bar">
                {BOTTOM_TABS.map(tab => (
                  <button key={tab.key}
                    className={`cd-tab-btn ${bottomTab === tab.key ? 'active' : ''}`}
                    onClick={() => { setBottomTab(tab.key); setBottomExpanded(true); }}>
                    {tab.label}
                  </button>
                ))}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                  <button className="cd-bottom-toggle" onClick={() => setBottomExpanded(!bottomExpanded)}>
                    {bottomExpanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                  </button>
                  <button className="cd-bottom-toggle" onClick={() => { setBottomTab(null); setBottomExpanded(false); }}>
                    <X size={14} />
                  </button>
                </div>
              </div>
              {bottomExpanded && (
                <div className="cd-bottom-content">
                  {bottomTab === 'hex' && <HexGridTab city={city} fmtNum={fmtNum} />}
                  {bottomTab === 'routes' && <RoutesTab city={city} fmtDate={fmtDate} fmtRel={fmtRel} onAddRoute={() => setShowAddRouteModal(true)} onDeleteRoute={handleDeleteRoute} />}
                  {bottomTab === 'calibrations' && <CalibrationsTab city={city} fmtDate={fmtDate} />}
                  {bottomTab === 'readings' && <ReadingsTab readings={readings} pagination={readingsPagination} setPagination={setReadingsPagination} onRefresh={fetchReadings} fmtDate={fmtDate} />}
                  {bottomTab === 'traffic' && <TrafficTab data={trafficDist} onRefresh={fetchTrafficDist} fmtNum={fmtNum} />}
                  {bottomTab === 'signals' && <SignalHealthTab data={signalFreshness} redisStats={redisStats} fmtNum={fmtNum} fmtRel={fmtRel} onRefresh={() => { setSignalFreshness(null); setRedisStats(null); fetchSignalFreshness(); fetchRedisStats(); }} onFlushRedis={handleFlushRedis} actionLoading={actionLoading} />}
                  {bottomTab === 'quality' && <DataQualityTab data={dataQuality} fmtNum={fmtNum} fmtRel={fmtRel} onRefresh={() => { setDataQuality(null); }} />}
                  {bottomTab === 'cost' && <CostTab data={costEstimate} fmtNum={fmtNum} />}
                </div>
              )}
            </div>
          )}

          {/* Floating tab opener */}
          {bottomTab === null && (
            <div className="cd-float-tabs">
              {BOTTOM_TABS.map(tab => (
                <button key={tab.key} className="cd-tab-btn"
                  onClick={() => { setBottomTab(tab.key); setBottomExpanded(true); }}>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && <EditCityModal city={city} onClose={() => setShowEditModal(false)} onSuccess={(msg) => { setMessage(msg); setShowEditModal(false); fetchCity(); }} onError={setError} />}
      {showAddRouteModal && <AddRouteModal cityId={cityId} onClose={() => setShowAddRouteModal(false)} onSuccess={(msg) => { setMessage(msg); setShowAddRouteModal(false); fetchCity(); }} onError={setError} />}

      {/* Pipeline Confirm */}
      {pipelineConfirm && (
        <PipelineConfirmModal
          action={pipelineConfirm}
          cityId={cityId}
          onClose={() => setPipelineConfirm(null)}
          onConfirm={() => handlePipeline(pipelineConfirm.action)}
          loading={!!actionLoading}
        />
      )}
    </div>
  );
};

const PipelineBtn = ({ action, label, icon, desc, variant, actionLoading, onConfirm }) => (
  <button
    className={`cd-btn ${variant === 'primary' ? 'cd-btn-primary' : ''} cd-btn-sm`}
    onClick={() => onConfirm({ action, label, desc })}
    disabled={!!actionLoading}>
    {icon} {actionLoading === action ? '...' : label}
  </button>
);

// ════════════════════════════════════════════════════════════════
// Tab: Hex Grid
// ════════════════════════════════════════════════════════════════
const HexGridTab = ({ city, fmtNum }) => {
  if (!city.hexStats || city.hexStats.length === 0) {
    return <div className="empty-state" style={{ padding: 40 }}>No hex grid data. Run "Seed Hexes" to generate the grid.</div>;
  }
  const totals = city.hexStats.reduce((acc, r) => ({
    total: acc.total + r.total, road: acc.road + r.road, highway: acc.highway + r.highway,
    arterial: acc.arterial + r.arterial, local: acc.local + r.local, unclassified: acc.unclassified + r.unclassified,
  }), { total: 0, road: 0, highway: 0, arterial: 0, local: 0, unclassified: 0 });
  const bbox = city.bbox || {};
  return (
    <div>
      <div className="data-table"><table><thead><tr>
        <th>Resolution</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Road</th>
        <th style={{ textAlign: 'right' }}>Highway</th><th style={{ textAlign: 'right' }}>Arterial</th>
        <th style={{ textAlign: 'right' }}>Local</th><th style={{ textAlign: 'right' }}>Unclassified</th><th style={{ textAlign: 'right' }}>% Road</th>
      </tr></thead><tbody>
        {city.hexStats.map(r => (
          <tr key={r.resolution}>
            <td><strong>res-{r.resolution}</strong></td>
            <td style={{ textAlign: 'right' }}>{fmtNum(r.total)}</td>
            <td style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(r.road)}</td>
            <td style={{ textAlign: 'right' }}>{fmtNum(r.highway)}</td><td style={{ textAlign: 'right' }}>{fmtNum(r.arterial)}</td>
            <td style={{ textAlign: 'right' }}>{fmtNum(r.local)}</td>
            <td style={{ textAlign: 'right', color: r.unclassified > 0 ? '#f59e0b' : '#64748b' }}>{fmtNum(r.unclassified)}</td>
            <td style={{ textAlign: 'right' }}>{r.total > 0 ? ((r.road / r.total) * 100).toFixed(1) + '%' : '—'}</td>
          </tr>
        ))}
        <tr style={{ fontWeight: 700, borderTop: '2px solid #334155' }}>
          <td>Total</td><td style={{ textAlign: 'right' }}>{fmtNum(totals.total)}</td><td style={{ textAlign: 'right' }}>{fmtNum(totals.road)}</td>
          <td style={{ textAlign: 'right' }}>{fmtNum(totals.highway)}</td><td style={{ textAlign: 'right' }}>{fmtNum(totals.arterial)}</td>
          <td style={{ textAlign: 'right' }}>{fmtNum(totals.local)}</td><td style={{ textAlign: 'right' }}>{fmtNum(totals.unclassified)}</td>
          <td style={{ textAlign: 'right' }}>{totals.total > 0 ? ((totals.road / totals.total) * 100).toFixed(1) + '%' : '—'}</td>
        </tr>
      </tbody></table></div>
      {(bbox.north || bbox.south) && (
        <div className="info-card" style={{ marginTop: 12 }}><strong>Bounding Box:</strong>{' '}N {bbox.north}° · S {bbox.south}° · E {bbox.east}° · W {bbox.west}°</div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Routes
// ════════════════════════════════════════════════════════════════
const RoutesTab = ({ city, fmtRel, onAddRoute, onDeleteRoute }) => {
  const routes = city.routes || [];
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{routes.filter(r => !r.isCandidate).length} sentinels, {routes.filter(r => r.isCandidate).length} candidates</span>
        <button className="cd-btn cd-btn-primary cd-btn-sm" onClick={onAddRoute}>+ Add Route</button>
      </div>
      {routes.length === 0 ? <div className="empty-state" style={{ padding: 40 }}>No sentinel routes.</div> : (
        <div className="data-table"><table><thead><tr>
          <th>Route</th><th>Classification</th><th>Role</th><th style={{ textAlign: 'right' }}>Error (pp)</th>
          <th style={{ textAlign: 'right' }}>Priority</th><th style={{ textAlign: 'right' }}>Readings</th><th>Last Polled</th><th>Actions</th>
        </tr></thead><tbody>
          {routes.map(route => (
            <tr key={route.id}>
              <td><div><div style={{ fontWeight: 600 }}>{route.routeName}</div>
                <div style={{ fontSize: '0.65rem', color: '#64748b' }}>{parseFloat(route.originLat).toFixed(3)}° → {parseFloat(route.destLat).toFixed(3)}°</div></div></td>
              <td><span className={`status-badge ${route.roadClassification === 'highway' ? 'status-active' : route.roadClassification === 'arterial' ? 'status-pending' : 'status-disabled'}`}>{route.roadClassification}</span></td>
              <td>{!route.isCandidate ? <span className="status-badge status-active">Sentinel</span> : <span className="status-badge status-disabled">Candidate</span>}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{route.calibrationErrorPp != null ? route.calibrationErrorPp.toFixed(2) : '—'}</td>
              <td style={{ textAlign: 'right' }}>{route.priority || '—'}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(route.readingCount || 0).toLocaleString()}</td>
              <td style={{ fontSize: '0.75rem' }}>{fmtRel(route.lastPolledAt || route.lastReadingAt)}</td>
              <td><button className="cd-btn cd-btn-danger cd-btn-sm" onClick={() => onDeleteRoute(route.id, route.routeName)}>Remove</button></td>
            </tr>
          ))}
        </tbody></table></div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Calibrations
// ════════════════════════════════════════════════════════════════
const CalibrationsTab = ({ city, fmtDate }) => {
  const calibrations = city.calibrations || [];
  if (calibrations.length === 0) return <div className="empty-state" style={{ padding: 40 }}>No calibration runs yet.</div>;
  return (
    <div className="data-table"><table><thead><tr>
      <th>Run</th><th>Date</th><th>Status</th><th style={{ textAlign: 'right' }}>k</th><th>Selected</th>
      <th style={{ textAlign: 'right' }}>Mean Error</th><th style={{ textAlign: 'right' }}>Usable %</th><th style={{ textAlign: 'right' }}>Exact %</th><th style={{ textAlign: 'right' }}>Cost</th>
    </tr></thead><tbody>
      {calibrations.map((cal, idx) => (
        <tr key={cal.id}>
          <td>#{calibrations.length - idx}</td>
          <td style={{ fontSize: '0.75rem' }}>{fmtDate(cal.createdAt)}</td>
          <td><span className={`status-badge ${cal.status === 'completed' ? 'status-active' : cal.status === 'running' ? 'status-pending' : 'status-disabled'}`}>{cal.status}</span></td>
          <td style={{ textAlign: 'right' }}>{cal.k ?? '—'}</td>
          <td style={{ fontSize: '0.75rem' }}>{cal.selectedSentinels && Array.isArray(cal.selectedSentinels) ? cal.selectedSentinels.join(', ') : '—'}</td>
          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{cal.meanErrorPp != null ? `${cal.meanErrorPp.toFixed(2)} pp` : '—'}</td>
          <td style={{ textAlign: 'right', fontWeight: 600 }}>{cal.catUsablePct != null ? `${cal.catUsablePct.toFixed(1)}%` : '—'}</td>
          <td style={{ textAlign: 'right' }}>{cal.catExactPct != null ? `${cal.catExactPct.toFixed(1)}%` : '—'}</td>
          <td style={{ textAlign: 'right' }}>{cal.costUsd != null ? `$${cal.costUsd.toFixed(2)}` : '—'}</td>
        </tr>
      ))}
    </tbody></table></div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Readings
// ════════════════════════════════════════════════════════════════
const ReadingsTab = ({ readings, pagination, setPagination, onRefresh, fmtDate }) => {
  if (!readings) return <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading...</div>;
  const congClr = (l) => l === 'free_flow' || l === 'light' ? 'status-active' : l === 'moderate' ? 'status-pending' : 'status-disabled';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{pagination.totalCount} readings</span>
        <button className="cd-btn cd-btn-sm" onClick={onRefresh}>Refresh</button>
      </div>
      {readings.length === 0 ? <div className="empty-state" style={{ padding: 40 }}>No readings yet.</div> : (<>
        <div className="data-table"><table><thead><tr>
          <th>Route</th><th>Class</th><th>Polled</th><th style={{ textAlign: 'right' }}>Duration</th>
          <th style={{ textAlign: 'right' }}>Static</th><th style={{ textAlign: 'right' }}>Delay</th><th>Congestion</th>
        </tr></thead><tbody>
          {readings.map(r => (
            <tr key={r.id}>
              <td style={{ fontWeight: 600 }}>{r.routeName}</td>
              <td><span className={`status-badge ${r.roadClassification === 'highway' ? 'status-active' : r.roadClassification === 'arterial' ? 'status-pending' : 'status-disabled'}`}>{r.roadClassification}</span></td>
              <td style={{ fontSize: '0.75rem' }}>{fmtDate(r.polledAt)}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.durationSec}s</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.staticDurationSec}s</td>
              <td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.delayFraction != null ? (parseFloat(r.delayFraction) >= 0 ? '+' : '') + parseFloat(r.delayFraction).toFixed(3) : '—'}</td>
              <td><span className={`status-badge ${congClr(r.congestionLevel)}`}>{(r.congestionLevel || 'unknown').replace('_', ' ')}</span></td>
            </tr>
          ))}
        </tbody></table></div>
        {pagination.totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: '0.8rem', color: '#94a3b8' }}>
            <span>Page {pagination.page}/{pagination.totalPages}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="cd-btn cd-btn-sm" disabled={pagination.page <= 1} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>← Prev</button>
              <button className="cd-btn cd-btn-sm" disabled={pagination.page >= pagination.totalPages} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>Next →</button>
            </div>
          </div>
        )}
      </>)}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Traffic
// ════════════════════════════════════════════════════════════════
const TrafficTab = ({ data, onRefresh, fmtNum }) => {
  if (!data) return <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading...</div>;
  const { distribution, hours } = data;
  const total = distribution.reduce((sum, d) => sum + d.count, 0);
  const byCategory = {};
  distribution.forEach(d => { const k = d.category || 'uncategorized'; byCategory[k] = (byCategory[k] || 0) + d.count; });
  const catColors = { free_flow: '#22c55e', light: '#84cc16', moderate: '#f59e0b', heavy: '#ef4444', standstill: '#991b1b', uncategorized: '#64748b' };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Last {hours}h · {fmtNum(total)} snapshots</span>
        <button className="cd-btn cd-btn-sm" onClick={onRefresh}>Refresh</button>
      </div>
      {total === 0 ? <div className="empty-state" style={{ padding: 40 }}>No traffic data.</div> : (<>
        <div style={{ display: 'flex', height: 24, borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
          {Object.entries(byCategory).map(([cat, count]) => (
            <div key={cat} style={{ width: `${(count / total) * 100}%`, background: catColors[cat] || '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.6rem', fontWeight: 600, minWidth: count > 0 ? 20 : 0 }}
              title={`${cat}: ${count.toLocaleString()}`}>
              {((count / total) * 100) > 5 ? `${((count / total) * 100).toFixed(0)}%` : ''}
            </div>
          ))}
        </div>
        <div className="data-table"><table><thead><tr><th>Category</th><th style={{ textAlign: 'right' }}>Count</th><th style={{ textAlign: 'right' }}>%</th></tr></thead><tbody>
          {distribution.map((d, i) => (
            <tr key={i}><td><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: catColors[d.category] || '#64748b', marginRight: 8 }} />{d.category || 'uncategorized'}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{d.count.toLocaleString()}</td>
              <td style={{ textAlign: 'right' }}>{((d.count / total) * 100).toFixed(1)}%</td></tr>
          ))}
        </tbody></table></div>
      </>)}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Signal Health (detailed)
// ════════════════════════════════════════════════════════════════
const SignalHealthTab = ({ data, redisStats, fmtNum, fmtRel, onRefresh, onFlushRedis, actionLoading }) => {
  if (!data) return <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading...</div>;
  const { signals, hexCounts, totalHexes } = data;
  const sIcons = { weather: '🌤', traffic_density: '🚗', aqi: '💨', scene_context: '📍' };
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{totalHexes.toLocaleString()} hexes · {signals.length} signals</span>
        <button className="cd-btn cd-btn-sm" onClick={onRefresh}>Refresh</button>
      </div>
      <div className="detail-grid" style={{ marginBottom: 20 }}>
        {signals.map(s => (
          <div key={s.signalType} className="detail-item" style={{ borderLeftColor: s.missedRuns > 5 ? '#ef4444' : s.missedRuns > 0 ? '#f59e0b' : '#22c55e' }}>
            <label>{sIcons[s.signalType]} {s.signalType.replace('_', ' ')}</label>
            <span>{fmtNum(s.active)} active</span>
            <div className="detail-item-sub">{s.coveragePct}% · Res-{s.nativeResolution} · {fmtRel(s.lastSnapshot)}</div>
          </div>
        ))}
      </div>
      <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Snapshot Age Distribution</h4>
      <div className="data-table" style={{ marginBottom: 20 }}>
        <table><thead><tr>
          <th>Signal</th><th style={{ textAlign: 'right' }}>Active</th><th style={{ textAlign: 'right' }}>{'<1h'}</th>
          <th style={{ textAlign: 'right' }}>1-6h</th><th style={{ textAlign: 'right' }}>6-24h</th>
          <th style={{ textAlign: 'right' }}>1-7d</th><th style={{ textAlign: 'right' }}>{'>7d'}</th><th style={{ textAlign: 'right' }}>Expired</th>
        </tr></thead><tbody>
          {signals.map(s => (
            <tr key={s.signalType}>
              <td><strong>{sIcons[s.signalType]} {s.signalType}</strong></td>
              <td style={{ textAlign: 'right', color: '#22c55e', fontWeight: 600 }}>{fmtNum(s.active)}</td>
              <td style={{ textAlign: 'right' }}>{fmtNum(s.staleness.last1h)}</td>
              <td style={{ textAlign: 'right' }}>{fmtNum(s.staleness.last1hTo6h)}</td>
              <td style={{ textAlign: 'right' }}>{fmtNum(s.staleness.last6hTo24h)}</td>
              <td style={{ textAlign: 'right' }}>{fmtNum(s.staleness.last1dTo7d)}</td>
              <td style={{ textAlign: 'right', color: s.staleness.older7d > 0 ? '#ef4444' : '#64748b' }}>{fmtNum(s.staleness.older7d)}</td>
              <td style={{ textAlign: 'right', color: '#64748b' }}>{fmtNum(s.expired)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Hex Inventory</h4>
      <div className="detail-grid" style={{ marginBottom: 20 }}>
        {Object.entries(hexCounts).map(([res, count]) => (
          <div key={res} className="detail-item"><label>Res-{res}</label><span>{count.toLocaleString()}</span></div>
        ))}
      </div>
      {redisStats && (<>
        <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Redis</h4>
        <div className="detail-grid" style={{ marginBottom: 12 }}>
          <div className="detail-item"><label>Memory</label><span>{redisStats.memory.usedMemory}</span></div>
          <div className="detail-item"><label>Hex Keys</label><span>{fmtNum(redisStats.keys.totalHexKeys)}</span></div>
          <div className="detail-item"><label>Hit Rate</label><span style={{ color: '#22c55e' }}>{redisStats.stats.hitRate || 'N/A'}</span></div>
          <div className="detail-item"><label>Evictions</label><span>{fmtNum(parseInt(redisStats.stats.evictedKeys || 0))}</span></div>
        </div>
        <button className="cd-btn cd-btn-danger cd-btn-sm" onClick={onFlushRedis} disabled={actionLoading === 'flush-redis'}>
          {actionLoading === 'flush-redis' ? 'Flushing...' : 'Flush City Cache'}
        </button>
      </>)}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Data Quality
// ════════════════════════════════════════════════════════════════
const DataQualityTab = ({ data, fmtNum, fmtRel, onRefresh }) => {
  if (!data) return <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading...</div>;
  const { enrichment, labels, signalGaps, sceneFreshness, partitions } = data;
  const totalHexes = enrichment.reduce((a, r) => a + r.total, 0);
  const totalNeverEnriched = enrichment.reduce((a, r) => a + r.neverEnriched, 0);
  const totalUnclassifiedRoad = enrichment.reduce((a, r) => a + r.unclassifiedRoad, 0);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Data quality report</span>
        <button className="cd-btn cd-btn-sm" onClick={onRefresh}>Refresh</button>
      </div>
      <div className="detail-grid" style={{ marginBottom: 20 }}>
        <div className="detail-item" style={{ borderLeftColor: totalNeverEnriched > 0 ? '#f59e0b' : '#22c55e' }}>
          <label>Unenriched</label><span style={{ color: totalNeverEnriched > 0 ? '#f59e0b' : '#22c55e' }}>{fmtNum(totalNeverEnriched)}</span></div>
        <div className="detail-item" style={{ borderLeftColor: totalUnclassifiedRoad > 0 ? '#f59e0b' : '#22c55e' }}>
          <label>Unclassified</label><span>{fmtNum(totalUnclassifiedRoad)}</span></div>
        <div className="detail-item"><label>Labels</label><span>{labels.length} types</span>
          <div className="detail-item-sub">{fmtNum(labels.reduce((a, l) => a + l.labelCount, 0))} total</div></div>
      </div>
      <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Enrichment by Resolution</h4>
      <div className="data-table" style={{ marginBottom: 20 }}>
        <table><thead><tr>
          <th>Res</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Enriched</th>
          <th style={{ textAlign: 'right' }}>Never</th><th style={{ textAlign: 'right' }}>Road</th><th style={{ textAlign: 'right' }}>Unclassified</th><th>Last</th>
        </tr></thead><tbody>
          {enrichment.map(r => (
            <tr key={r.resolution}>
              <td><strong>R-{r.resolution}</strong></td>
              <td style={{ textAlign: 'right' }}>{fmtNum(r.total)}</td>
              <td style={{ textAlign: 'right', color: '#22c55e' }}>{fmtNum(r.enriched)}</td>
              <td style={{ textAlign: 'right', color: r.neverEnriched > 0 ? '#f59e0b' : '#64748b' }}>{fmtNum(r.neverEnriched)}</td>
              <td style={{ textAlign: 'right' }}>{fmtNum(r.roadHexes)}</td>
              <td style={{ textAlign: 'right', color: r.unclassifiedRoad > 0 ? '#f59e0b' : '#64748b' }}>{fmtNum(r.unclassifiedRoad)}</td>
              <td style={{ fontSize: '0.7rem' }}>{fmtRel(r.lastEnrichment)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Signal Gaps</h4>
      <div className="data-table" style={{ marginBottom: 20 }}>
        <table><thead><tr>
          <th>Res</th><th style={{ textAlign: 'right' }}>No Weather</th><th style={{ textAlign: 'right' }}>No Traffic</th>
          <th style={{ textAlign: 'right' }}>No AQI</th><th style={{ textAlign: 'right' }}>No Scene</th>
        </tr></thead><tbody>
          {signalGaps.map(r => (
            <tr key={r.resolution}>
              <td><strong>R-{r.resolution}</strong></td>
              <td style={{ textAlign: 'right', color: r.noWeather > 0 ? '#f59e0b' : '#22c55e' }}>{fmtNum(r.noWeather)}</td>
              <td style={{ textAlign: 'right', color: r.noTraffic > 0 ? '#f59e0b' : '#22c55e' }}>{fmtNum(r.noTraffic)}</td>
              <td style={{ textAlign: 'right', color: r.noAqi > 0 ? '#f59e0b' : '#22c55e' }}>{fmtNum(r.noAqi)}</td>
              <td style={{ textAlign: 'right', color: r.noScene > 0 ? '#f59e0b' : '#22c55e' }}>{fmtNum(r.noScene)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      {sceneFreshness && (<>
        <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Scene Freshness</h4>
        <div className="detail-grid" style={{ marginBottom: 20 }}>
          <div className="detail-item" style={{ borderLeftColor: '#22c55e' }}><label>{'<30d'}</label><span>{fmtNum(sceneFreshness.fresh30d)}</span></div>
          <div className="detail-item" style={{ borderLeftColor: '#f59e0b' }}><label>30-60d</label><span>{fmtNum(sceneFreshness.stale30to60d)}</span></div>
          <div className="detail-item" style={{ borderLeftColor: '#ef4444' }}><label>60-90d</label><span>{fmtNum(sceneFreshness.stale60to90d)}</span></div>
          <div className="detail-item" style={{ borderLeftColor: '#991b1b' }}><label>{'>90d'}</label><span>{fmtNum(sceneFreshness.veryStale90d)}</span></div>
        </div>
      </>)}
      <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Labels ({labels.length} types)</h4>
      <div className="data-table" style={{ marginBottom: 20 }}>
        <table><thead><tr>
          <th>Type</th><th style={{ textAlign: 'right' }}>Hexes</th><th style={{ textAlign: 'right' }}>Labels</th>
          <th style={{ textAlign: 'right' }}>Stale</th><th>Verified</th>
        </tr></thead><tbody>
          {labels.map(l => (
            <tr key={l.labelType}>
              <td><strong>{l.labelType}</strong></td>
              <td style={{ textAlign: 'right' }}>{fmtNum(l.hexCount)}</td>
              <td style={{ textAlign: 'right' }}>{fmtNum(l.labelCount)}</td>
              <td style={{ textAlign: 'right', color: l.staleLabels > 0 ? '#f59e0b' : '#64748b' }}>{fmtNum(l.staleLabels)}</td>
              <td style={{ fontSize: '0.7rem' }}>{fmtRel(l.lastVerified)}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      {partitions && partitions.length > 0 && (<>
        <h4 style={{ fontSize: '0.8rem', marginBottom: 8, color: '#94a3b8' }}>Partitions</h4>
        <div className="data-table">
          <table><thead><tr><th>Name</th><th>Size</th></tr></thead><tbody>
            {partitions.map(p => (<tr key={p.name}><td>{p.name}</td><td>{p.size}</td></tr>))}
          </tbody></table>
        </div>
      </>)}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// Tab: Cost
// ════════════════════════════════════════════════════════════════
const CostTab = ({ data, fmtNum }) => {
  if (!data) return <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Loading...</div>;
  return (
    <div>
      <div className="detail-grid" style={{ marginBottom: 20 }}>
        <div className="detail-item"><label>Poll Interval</label><span>{data.pollIntervalMinutes}m</span>
          <div className="detail-item-sub">{data.pollsPerDay}/day</div></div>
        <div className="detail-item"><label>Sentinels</label><span>{data.sentinelCount}</span>
          <div className="detail-item-sub">{fmtNum(data.apiCallsPerMonth)} calls/mo</div></div>
        <div className="detail-item"><label>Google Routes</label><span>${data.estimatedMonthlyCost.api}/mo</span></div>
        <div className="detail-item"><label>Lambda</label><span>${data.estimatedMonthlyCost.lambda}/mo</span></div>
        <div className="detail-item" style={{ borderLeftColor: '#3b82f6' }}><label>Total</label>
          <span style={{ color: '#3b82f6', fontSize: '1.1rem' }}>${data.estimatedMonthlyCost.total}/mo</span></div>
      </div>
      <div className="info-card"><strong>Optimize:</strong> 6min→~$124/mo · 12min→~$66/mo · 30min→~$28/mo</div>
    </div>
  );
};

export default CityDetail;
